import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { MDSButton, MDSIcon, MDSImage, MDSTypography } from '@marqvision/mds-v2';
import { SIZE } from './@constants';

const useStyles = makeStyles(({ _raw_color }) => ({
  wrapper: {
    position: 'relative',
    minWidth: '276px',
    minHeight: '107px',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
  },
  section: {
    padding: '16px',
    display: 'grid',
    gridTemplateRows: '1fr auto',
    justifyItems: 'flex-start',
    gap: '8px',
  },
  content: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    justifyItems: 'flex-start',
    gap: '6px',
  },
  mirror: {
    position: 'absolute',
    visibility: 'hidden',
  },
  blank: {
    width: '28px',
  },
  close: {
    top: '8px',
    right: '8px',
    position: 'absolute',
    padding: '4px',
    border: 'none',
    backgroundColor: _raw_color.whiteAlpha40,
    borderRadius: '50%',
    '&.isClickable': {
      cursor: 'pointer',
    },
  },
}));

const DELAY = 0;

type Props = {
  title: string;
  content: string;
  image?: {
    isLoading?: boolean;
    url: string;
  };
  actionLabel: string;
  onWidthChange: (width: number) => void;
  onExplorer?: () => void;
  onClose?: () => void;
};

export const ReleaseNotePopupBox = (props: Props) => {
  const { title, content, image, actionLabel, onExplorer, onClose } = props;
  const classes = useStyles();

  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const onWidthChange = useRef(props.onWidthChange);

  const contentMirrorWidth = Math.max(titleRef.current?.clientWidth || 0, SIZE.title.minWidth);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      if (!titleRef.current || !contentRef.current) return;
      const titleWidth = titleRef.current.clientWidth + 1;
      const contentHeight = contentRef.current.clientHeight + 1;
      const totalHeight =
        contentHeight > SIZE.content.maxHeight
          ? SIZE.title.maxWidth
          : clamp(SIZE.title.minWidth, titleWidth, SIZE.title.maxWidth);
      const imageWidth = image?.url ? SIZE.image.width : SIZE.blank.width;
      const wrapperWidth = SIZE.padding + totalHeight + imageWidth;
      onWidthChange.current(wrapperWidth);
    }, DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [title, content, image?.url]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.section}>
        <div className={classes.content}>
          <div ref={titleRef} className={classes.mirror}>
            <MDSTypography variant="title" size="s" weight="semibold" whiteSpace="nowrap" style={{ fontWeight: 640 }}>
              {title}
            </MDSTypography>
          </div>
          <div ref={contentRef} className={classes.mirror} style={{ width: contentMirrorWidth }}>
            <MDSTypography size="s" wordBreak="break-word">
              {content}
            </MDSTypography>
          </div>
          <MDSTypography
            variant="title"
            size="s"
            weight="semibold"
            lineClamp={1}
            wordBreak="break-word"
            color={title ? undefined : 'color/content/neutral/default/disabled'}
            style={{ fontWeight: 640 }}
          >
            {title || 'Title'}
          </MDSTypography>
          <MDSTypography
            size="s"
            lineClamp={2}
            wordBreak="break-word"
            color={content ? undefined : 'color/content/neutral/default/disabled'}
          >
            {content || 'Content'}
          </MDSTypography>
        </div>
        <MDSButton variant="fill" size="small" color="blue" onClick={onExplorer}>
          {actionLabel}
        </MDSButton>
      </div>
      {image ? (
        <MDSImage removeBorderRadius isLoading={image.isLoading} width="232px" height="131px" src={image.url} />
      ) : (
        <div className={classes.blank} />
      )}
      <button className={clsx(classes.close, { isClickable: onClose })} onClick={onClose}>
        <MDSIcon.CloseDelete variant="outline" size={16} />
      </button>
    </div>
  );
};

const clamp = (min: number, value: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};
